import { useForm } from 'react-hook-form';
import Input from '../../../components/Input/Input';
import Button from '../../../components/new-design/Button';
import { validationSchema } from '../validations/auth.validation';
import { yupResolver } from '@hookform/resolvers/yup';
import ErrorText from '../../../components/ErrorText';
import { useMemo, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import ROUTES from '../../../routers/Routes';
import { useLoginWithEmail, usePrivy } from '@privy-io/react-auth';
import CodeInput from './CodeInput';
import { setReferralCode } from '@/features/auth/utils/referral-code';
import { twMerge } from 'tailwind-merge';

interface FormInputs {
  email: string;
}
const excludedRoutes = [ROUTES.LOGIN, ROUTES.REGISTER, ROUTES.KYC, ROUTES.AVATURN];

const AuthForm = () => {
  const { authenticated, ready } = usePrivy();
  const { sendCode } = useLoginWithEmail();
  const [searchParams] = useSearchParams();

  const referralCode = searchParams.get('r');
  const [isCodeSent, setIsCodeSent] = useState<boolean>(false);

  const { state: locationState } = useLocation();
  const redirectTo = useMemo(() => {
    if (!!locationState?.redirectTo?.pathname && !excludedRoutes.includes(locationState?.redirectTo?.pathname)) {
      return locationState.redirectTo?.pathname;
    }
    return ROUTES.LOBBY;
  }, [locationState]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const onSubmit = async ({ email }: FormInputs) => {
    try {
      await sendCode({ email });
      if (referralCode) setReferralCode(referralCode);
      setIsCodeSent(true);
    } catch (_) {
      setIsCodeSent(false);
    }
  };

  if (!ready) return null;

  return authenticated ? (
    <Navigate to={redirectTo} />
  ) : (
    <div className="scrollbar-phenom z-10 mx-auto flex h-full w-full max-w-[500px] flex-col overflow-auto py-8 md:min-w-96">
      <h1 className="mb-10 text-center text-2xl font-semibold text-ph-gray-white">Log in or Sign Up</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="mb-5 flex flex-col gap-8">
        <div className="flex flex-col gap-1">
          <Input
            type="text"
            placeholder="Enter Your Email Address"
            {...register('email')}
            fullWidth
            error={!!errors.email}
            autoComplete="off"
            disabled={isCodeSent}
            className={twMerge(isCodeSent && '!text-white')}
          />
          {errors.email?.message && <ErrorText>* {errors.email.message}</ErrorText>}
        </div>

        {!isCodeSent && (
          <div className="flex flex-col gap-4">
            <Button fullWidth type="submit">
              Submit
            </Button>
          </div>
        )}
      </form>

      {isCodeSent && (
        <div className="flex flex-col gap-5">
          <CodeInput />
          <p className="text-center text-xs font-semibold text-ph-gray-white">A Code has been sent to your email!</p>
        </div>
      )}
    </div>
  );
};

export default AuthForm;
